import {
  LeftHeader,
  MessageData,
  MessageContent,
  MessageHeader,
  Box,
  MessageDetail,
  Name,
  Icon,
  NewChat,
  RightChat,
  SectionHeading,
  UnpinChat,
  Section,
  DroppableContainer,
  SortableItemWrapper
} from './styles';
import { ChatGroupInterface } from '../../../interfaces/chatMessageInterface';
import {
  CHAT_GROUP_TYPE,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_COLORS,
  PROJECT_ICON_TYPE
} from '../../../global/constants';
import ChatLoading from '../../loading/chatLoading';
import { isEmpty } from '../../../helpers/common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { updateChatGroupOrder } from '../../../services/chatMessageService';
import ChatBoxAvatar from '../../avatar/chatboxAvatar';
import { useMobileDevice } from '../../../global/useMobile';
import ResponsiveHeader from '../../responsiveHeader';
import BottomSubNavigation from '../../bottomNavigation/subNavigation';
import BottomNavigationItem from '../../bottomNavigation/subNavigation/bottomNavigationItem';
import ProjectIconAvatar from '../../projectIconAvatar';
import { rootStore } from '../../../mobx/rootStore';
import { ProjectModel } from '../../../mobx/models/project';
import IconButton from '../../button/iconButton';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../emptyState';
import { ROUTES } from '../../../routes/urls';
import Image from '../../image';
import { captureException } from '../../../services/logService';

// Lean groups carry a lastMessage summary instead of embedded messages.
const hasMessages = (group: Pick<ChatGroupInterface, 'lastMessage' | 'messages'>) =>
  group?.lastMessage !== undefined ? !!group.lastMessage : !isEmpty(group?.messages);

interface ChatGroupRowProps {
  group: ChatGroupInterface;
  isMobile: boolean;
  isActive: boolean;
  forceHover?: boolean;
  onClick?: () => void;
}

const ChatGroupRow: React.FC<ChatGroupRowProps> = ({ group, isMobile, isActive, forceHover, onClick }) => {
  const { projectStore } = rootStore;
  const { projects } = projectStore;
  const isProject = group?.type === CHAT_GROUP_TYPE.PROJECT;
  let project: ProjectModel | null = null;
  if (isProject) {
    project = projects?.find((item) => item?.id === group?.id) || null;
  }

  return (
    <Box role='button' isActive={isActive} forceHover={forceHover} onClick={onClick}>
      <RightChat>
        <Icon>
          {isProject && (
            <ProjectIconAvatar
              size={isMobile ? 26 : 20}
              type={project?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
              reference={project?.icon?.reference || ''}
              backgroundColor={ICON_COLORS[project?.icon?.color || 1]}
              projectName={project?.name || ''}
              borderRadius={'50px'}
              iconSize={isMobile ? 16 : 13}
            />
          )}
          {group?.users && !isProject && (
            <ChatBoxAvatar
              imgSrc={group?.profilePic || ''}
              name={group?.users[0]?.name}
              size={isMobile ? 26 : 20}
              users={group?.users}
            />
          )}
        </Icon>
        <MessageDetail>
          <Name hasUnreadMessages={group?.hasUnreadMessages}>{group?.name || ''}</Name>
        </MessageDetail>
      </RightChat>
    </Box>
  );
};

interface SortableChatGroupItemProps {
  group: ChatGroupInterface;
  isActive: boolean;
  isMobile: boolean;
  onClickChatGroup: (group: ChatGroupInterface) => void;
}

const SortableChatGroupItem: React.FC<SortableChatGroupItemProps> = ({
  group,
  isActive,
  isMobile,
  onClickChatGroup
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group?.id });
  const cssTransform = CSS.Translate.toString(transform ? { ...transform } : null) || undefined;

  return (
    <SortableItemWrapper
      ref={setNodeRef}
      isDragging={isDragging}
      cssTransform={cssTransform}
      cssTransition={transition || undefined}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...attributes}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...listeners}>
      <ChatGroupRow
        group={group}
        isMobile={isMobile}
        isActive={isActive}
        onClick={() => onClickChatGroup(group)}
      />
    </SortableItemWrapper>
  );
};

interface Props {
  onClickNewChat: () => void;
  onClickChatGroup: (group: ChatGroupInterface) => void;
  newGroup: boolean;
  loading: boolean;
}

const ChatBox = ({ onClickNewChat, onClickChatGroup, newGroup, loading }: Props) => {
  const isMobile = useMobileDevice();

  // mobx variables
  const { chatMessageStore } = rootStore;
  const { pinChatGroups, unpinChatGroups, currentChatGroup } = chatMessageStore;

  const mobile = useMobileDevice();
  const [activeChatGroupId, setActiveChatGroupId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const sortableIds = useMemo(() => pinChatGroups.map((group) => group.id), [pinChatGroups]);

  const isEmptyChatGroupData = useMemo(() => {
    const nonEmptyPinChatGroups = pinChatGroups.filter(hasMessages);
    const nonEmptyUnpinChatGroups = unpinChatGroups.filter(hasMessages);
    return nonEmptyPinChatGroups.length === 0 && nonEmptyUnpinChatGroups.length === 0;
  }, [pinChatGroups, unpinChatGroups]);

  const calculateUpdatedGroupIndex = useCallback(
    (destinationIndex: number, sourceIndex: number) => {
      let updatedGroupIndex;
      if (destinationIndex === 0) {
        const nextGroupIndex = pinChatGroups?.[0]?.order;
        updatedGroupIndex = nextGroupIndex ? nextGroupIndex - 500 : new Date().getTime();
      } else if (destinationIndex === pinChatGroups?.length - 1) {
        const previousGroupIndex = pinChatGroups?.[pinChatGroups?.length - 1]?.order;
        updatedGroupIndex = previousGroupIndex ? previousGroupIndex + 500 : new Date().getTime();
      } else {
        if (destinationIndex > sourceIndex) {
          const previousGroupIndex: number = pinChatGroups?.[destinationIndex]?.order || 0;
          const nextGroupIndex: number = pinChatGroups?.[destinationIndex + 1]?.order || 0;
          updatedGroupIndex = (previousGroupIndex + nextGroupIndex) / 2;
        } else {
          const previousGroupIndex: number = pinChatGroups?.[destinationIndex - 1]?.order || 0;
          const nextGroupIndex: number = pinChatGroups?.[destinationIndex]?.order || 0;
          updatedGroupIndex = (previousGroupIndex + nextGroupIndex) / 2;
        }
      }
      return updatedGroupIndex;
    },
    [pinChatGroups]
  );

  const activeChatGroup = useMemo(
    () => pinChatGroups.find((group) => group.id === activeChatGroupId) || null,
    [activeChatGroupId, pinChatGroups]
  );

  useEffect(() => {
    if (!activeChatGroupId) return undefined;

    const { cursor } = document.body.style;
    document.body.style.cursor = 'grabbing';

    return () => {
      document.body.style.cursor = cursor;
    };
  }, [activeChatGroupId]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveChatGroupId(event.active.id as string);
  }, []);

  const handleDragCancel = useCallback((_event: DragCancelEvent) => {
    setActiveChatGroupId(null);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      try {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const sourceIndex = pinChatGroups.findIndex((group) => group.id === active.id);
        const destinationIndex = pinChatGroups.findIndex((group) => group.id === over.id);
        if (sourceIndex === -1 || destinationIndex === -1) return;

        const updatedGroupIndex = calculateUpdatedGroupIndex(destinationIndex, sourceIndex);
        const draggableId = active.id as string;

        const updatedPinGroups = pinChatGroups.map((chatGroup) =>
          chatGroup?.id === draggableId ? { ...(chatGroup || {}), order: updatedGroupIndex } : chatGroup
        );

        // Sort the updated list
        const sortedList = updatedPinGroups.toSorted((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
        // Update the state
        chatMessageStore.setPinChatGroups(sortedList);

        const payload = {
          Order: updatedGroupIndex
        };
        await updateChatGroupOrder(draggableId, payload);
      } catch (error) {
        captureException(error);
      } finally {
        setActiveChatGroupId(null);
      }
    },
    [calculateUpdatedGroupIndex, pinChatGroups, chatMessageStore]
  );

  const renderUnpinChatGroup = useMemo(
    () =>
      unpinChatGroups?.map((group) => {
        if (!hasMessages(group)) return null;
        const isActive = currentChatGroup?.id === group?.id;
        return (
          <ChatGroupRow
            key={group?.id}
            group={group}
            isMobile={isMobile}
            isActive={isActive}
            onClick={() => onClickChatGroup(group)}
          />
        );
      }),
    [currentChatGroup?.id, isMobile, onClickChatGroup, unpinChatGroups]
  );
  return (
    <>
      <MessageContent>
        {!mobile ? (
          <MessageHeader>
            <LeftHeader>Messages</LeftHeader>
            <IconButton
              iconName='new-chat-icon'
              size={ICON_BUTTON_SIZE.SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={onClickNewChat}
            />
          </MessageHeader>
        ) : (
          <ResponsiveHeader title='Messages' noMargin={true} />
        )}
        <MessageData>
          {pinChatGroups?.length > 0 && pinChatGroups.some(hasMessages) && (
            <Section>
              <SectionHeading>Pinned</SectionHeading>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  <DroppableContainer isMobile={mobile}>
                    {pinChatGroups.map((group) => {
                      if (!hasMessages(group)) return null;

                      const isActive = currentChatGroup?.id === group?.id;
                      return (
                        <SortableChatGroupItem
                          key={group?.id}
                          group={group}
                          isActive={isActive}
                          isMobile={mobile}
                          onClickChatGroup={onClickChatGroup}
                        />
                      );
                    })}
                  </DroppableContainer>
                </SortableContext>
                <DragOverlay>
                  {activeChatGroup ? (
                    <SortableItemWrapper isOverlay={true}>
                      <ChatGroupRow group={activeChatGroup} isMobile={mobile} isActive={false} forceHover={true} />
                    </SortableItemWrapper>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Section>
          )}
          {unpinChatGroups?.length > 0 && unpinChatGroups.some(hasMessages) && (
            <Section>
              {unpinChatGroups?.length > 0 && unpinChatGroups.some(hasMessages) && (
                <SectionHeading>Recent</SectionHeading>
              )}
              {newGroup && (
                <NewChat>
                  <Image imageKey='empty_profile' alt='emptyProfile' width={20} height={20} />
                  <Name>New chat</Name>
                </NewChat>
              )}
              {unpinChatGroups?.length > 0 && <UnpinChat>{renderUnpinChatGroup}</UnpinChat>}
            </Section>
          )}
          {/* loading state */}
          {loading && isEmptyChatGroupData && <ChatLoading />}
          {/* empty state */}
          {isEmptyChatGroupData && !loading && mobile && (
            <EmptyStateV2
              size={EMPTY_STATE_SIZE.MEDIUM}
              title='No messages yet'
              description='You have no messages at the moment'
              buttonTitle='New message'
              imageUrl={'message_empty'}
              onClick={onClickNewChat}
              isAlignedCenter={true}
            />
          )}
        </MessageData>
      </MessageContent>
      <BottomSubNavigation
        backNavigationPath={ROUTES.HOME}
        rightElement={
          <BottomNavigationItem
            iconName='new-chat-icon'
            onClick={onClickNewChat}
            disabled={loading && isEmptyChatGroupData}
          />
        }
      />
    </>
  );
};

export default observer(ChatBox);
